import { FC } from "react";
import { useNavigate } from "react-router-dom";
import i18next from "i18next";
import { AuthenticationProps } from "@/features/auth/types/authenticationTypes";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { auth } from "@/api/firebase/firebase.config";
import firebase from "firebase/compat/app";
import { AuthCodeForm } from "@/components/AuthCodeInputForm/AuthCodeForm";
import { siteRoutes } from "@/routes/siteRoutesList";
import { createToast } from "@/utils/toaster/createToast";
import { useVerificationCode } from "@/features/auth/hooks/useVerificationCode";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import QueryDB from "@/api/queryDB/common";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";

interface VerifyEmailCodeFormProps {
  emailAuthValues: {
    emailVal: string;
    passwordVal: string;
    passwordVerifVal: string;
  };
}

export const VerifyEmailCodeForm: FC<VerifyEmailCodeFormProps> = ({
  emailAuthValues,
}): JSX.Element => {
  const navigate = useNavigate();

  const currentUser = useCurrentUserProfile((s) => s.user);

  const baseAuthenticationProps = {
    method: "email",
    emailAddress: emailAuthValues.emailVal,
    platform: brand.brandValue,
    tenantId: brand.tenantId,
  } as AuthenticationProps;

  const handleUpdateEmail = async () => {
    if (auth.currentUser) {
      const credential = firebase.auth.EmailAuthProvider.credential(
        emailAuthValues.emailVal,
        emailAuthValues.passwordVal
      );
      try {
        await auth.currentUser.linkWithCredential(credential);
        await firebase.auth().currentUser?.reload();

        await QueryDB.updateProfileDoc(
          "profiles",
          auth.currentUser.uid,
          brand.tenantId,
          {
            ...currentUser,
            termsAccepted: true,
            email: emailAuthValues.emailVal,
          }
        );

        createToast({
          type: "success",
          message: "Successfully updated account",
        });
        navigate(siteRoutes["profile"]);
      } catch (error) {
        const message = (error as Error).message;
        createToast({
          type: "error",
          message,
        });
      }
    }
  };

  const { code, setFieldValue, loading, resendCode } = useVerificationCode({
    baseAuthenticationProps,
    action: "convert-to-permanent",
    onSuccess: () => handleUpdateEmail(),
  });

  // Page Document Title
  useDocumentTitle(i18next.t("auth.authcard.verify.headline"));

  return (
    <AuthCodeForm
      requestFunction={resendCode}
      loading={loading}
      code={code}
      setFieldValue={setFieldValue}
    />
  );
};
